import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BusinessUnit, CONFIG } from '@config';
import pkceChallenge from 'pkce-challenge';
import { firstValueFrom } from 'rxjs';

import { PkceStorageService } from './pkce-storage.service';
import { TokenService } from './token.service';

@Injectable()
export class AuthService {
  private readonly tokenService = inject(TokenService);
  private readonly pkceStorageService = inject(PkceStorageService);
  private readonly router = inject(Router);
  // Prevents concurrent 401 responses from the interceptor triggering multiple simultaneous login redirects
  private redirectInProgress = false;

  async handleAuthCallback(): Promise<void> {
    try {
      const params = new URLSearchParams(window.location.search);
      const authCode = params.get('authorization_code');

      if (authCode) {
        const codeVerifier = this.pkceStorageService.getVerifier();

        if (!codeVerifier) {
          console.error('[Auth] No PKCE code verifier found in session storage');
          this.pkceStorageService.clear();
          this.redirectInProgress = false;

          return;
        }

        try {
          const tokenResponse = await firstValueFrom(this.tokenService.exchangeCodeForToken(authCode, codeVerifier));

          this.tokenService.storeToken(tokenResponse.access_token);
          this.pkceStorageService.clear();
          await this.router.navigateByUrl('/', { replaceUrl: true });
        } catch (error) {
          console.error('[Auth] Token exchange failed:', error);
          this.pkceStorageService.clear();
        }

        this.redirectInProgress = false;
      } else {
        const token = this.tokenService.getToken();

        if (token) {
          try {
            const result = await firstValueFrom(this.tokenService.introspectToken(token));

            if (!result.active) {
              this.tokenService.clearToken();
              await this.initiateLogin();
            }
          } catch (error) {
            console.warn('[Auth] Token introspection failed, clearing token:', error);
            this.tokenService.clearToken();
            await this.initiateLogin();
          }
        }
      }
    } catch (error) {
      console.error('[Auth] Unexpected error in handleAuthCallback:', error);
    }
  }

  async initiateLogin(): Promise<void> {
    if (this.redirectInProgress) {
      return;
    }

    this.redirectInProgress = true;

    try {
      const { code_verifier, code_challenge } = await pkceChallenge();

      this.pkceStorageService.storeVerifier(code_verifier);

      const wasUrl = new URL(CONFIG.auth.webAuthSignInUrl);

      wasUrl.searchParams.set('codeChallenge', code_challenge);
      wasUrl.searchParams.set('redirectUrl', CONFIG.auth.redirectUri);
      wasUrl.searchParams.set('bu', BusinessUnit.us);

      window.location.href = wasUrl.toString();
    } catch (error) {
      this.redirectInProgress = false;
      console.error('[Auth] Failed to initiate login:', error);
    }
  }

  logout(): void {
    this.tokenService.clearToken();
    this.pkceStorageService.clear();
    window.location.href = '/';
  }
}
